import { useState, useEffect, useRef, useCallback } from 'react';
import config from '../config';

/**
 * Offline message queue with localStorage persistence, FIFO flush,
 * per-consultation blocking on failure, retry with limits, and 24h TTL.
 * Transport-agnostic via callback injection (DI).
 */
const useOfflineMessageQueue = ({ userId, sendMessage, getConsultationStatus, isOnline }) => {
  const [queuedMessages, setQueuedMessages] = useState([]);
  const isFlushing = useRef(false);
  const prevIsOnline = useRef(isOnline);

  const storageKey = `${config.chat.queue.storageKeyPrefix}_${userId}`;

  // --- localStorage helpers ---

  const readQueue = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[OfflineQueue] Corrupted localStorage, clearing:', e);
      try { localStorage.removeItem(storageKey); } catch (_) { /* silent */ }
      return [];
    }
  }, [storageKey]);

  const writeQueue = useCallback((entries) => {
    try {
      const active = entries.filter(e => e.status !== 'sent');
      localStorage.setItem(storageKey, JSON.stringify(active));
    } catch (e) {
      console.warn('[OfflineQueue] localStorage write failed:', e);
    }
  }, [storageKey]);

  // --- Core API ---

  const queueMessage = useCallback((consultationId, content, clientMessageId) => {
    const entry = {
      id: crypto.randomUUID(),
      consultationId,
      content,
      clientMessageId,
      status: 'pending',
      createdAt: new Date().toISOString(),
      retryCount: 0,
      lastError: null,
    };

    try {
      const current = readQueue();
      current.push(entry);
      writeQueue(current);
      setQueuedMessages(current.filter(e => e.status !== 'sent'));
      return entry;
    } catch (e) {
      console.warn('[OfflineQueue] Failed to queue message:', e);
      return null;
    }
  }, [readQueue, writeQueue]);

  const flushQueue = useCallback(async () => {
    if (isFlushing.current) return;
    isFlushing.current = true;

    try {
      let queue = readQueue();
      if (queue.length === 0) return;

      const maxRetries = config.chat.queue.maxRetries;
      const retryDelay = config.chat.queue.retryDelayMs;
      const maxRetentionMs = config.chat.queue.maxRetentionHours * 3600000;
      const now = Date.now();

      // TTL check — expire old messages
      queue = queue.map(entry => {
        if (entry.status === 'pending' && (now - Date.parse(entry.createdAt)) > maxRetentionMs) {
          return { ...entry, status: 'failed', lastError: 'Message expired (24h TTL)' };
        }
        return entry;
      });

      // Group by consultationId, preserving insertion order
      const consultationIds = [...new Set(
        queue.filter(e => e.status === 'pending').map(e => e.consultationId)
      )];

      for (const consultationId of consultationIds) {
        // Check consultation status (fail-open)
        let consultationActive = true;
        try {
          const statusResponse = await getConsultationStatus(consultationId);
          const status = statusResponse?.data?.data?.status || statusResponse?.data?.status || statusResponse;
          if (status === 'completed' || status === 'cancelled') {
            consultationActive = false;
          }
        } catch (e) {
          // Fail-open: assume active on network error
          console.warn('[OfflineQueue] Status check failed, assuming active:', e);
        }

        if (!consultationActive) {
          queue = queue.map(entry =>
            entry.consultationId === consultationId && entry.status === 'pending'
              ? { ...entry, status: 'failed', lastError: 'Consultation ended' }
              : entry
          );
          writeQueue(queue);
          continue;
        }

        // Flush this consultation's pending messages in FIFO order
        const pendingForConsultation = queue
          .filter(e => e.consultationId === consultationId && e.status === 'pending');

        let blocked = false;
        for (const entry of pendingForConsultation) {
          if (blocked) break;

          // Mark as sending
          queue = queue.map(e => e.id === entry.id ? { ...e, status: 'sending' } : e);
          writeQueue(queue);
          setQueuedMessages(queue.filter(e => e.status !== 'sent'));

          let sent = false;
          let currentRetry = entry.retryCount;

          while (currentRetry < maxRetries && !sent) {
            try {
              await sendMessage({
                consultationId: entry.consultationId,
                content: entry.content,
                clientMessageId: entry.clientMessageId,
              });
              sent = true;
            } catch (e) {
              currentRetry++;
              if (currentRetry < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            }
          }

          if (sent) {
            queue = queue.map(e => e.id === entry.id ? { ...e, status: 'sent' } : e);
          } else {
            queue = queue.map(e => e.id === entry.id
              ? { ...e, status: 'failed', retryCount: currentRetry, lastError: 'Send failed after retries' }
              : e
            );
            blocked = true; // STOP this consultation — FIFO blocking
          }

          writeQueue(queue);
          setQueuedMessages(queue.filter(e => e.status !== 'sent'));
        }
      }
    } finally {
      isFlushing.current = false;
    }
  }, [readQueue, writeQueue, sendMessage, getConsultationStatus]);

  const retryMessage = useCallback((messageId) => {
    let queue = readQueue();
    queue = queue.map(entry =>
      entry.id === messageId
        ? { ...entry, status: 'pending', retryCount: 0, lastError: null }
        : entry
    );
    writeQueue(queue);
    setQueuedMessages(queue.filter(e => e.status !== 'sent'));
    flushQueue();
  }, [readQueue, writeQueue, flushQueue]);

  // --- Effects ---

  // On mount: load queue, TTL check, initial flush
  useEffect(() => {
    if (!userId) return;

    let queue = readQueue();
    if (queue.length === 0) {
      setQueuedMessages([]);
      return;
    }

    // TTL check
    const maxRetentionMs = config.chat.queue.maxRetentionHours * 3600000;
    const now = Date.now();
    queue = queue.map(entry => {
      if (entry.status === 'pending' && (now - Date.parse(entry.createdAt)) > maxRetentionMs) {
        return { ...entry, status: 'failed', lastError: 'Message expired (24h TTL)' };
      }
      return entry;
    });
    writeQueue(queue);
    setQueuedMessages(queue.filter(e => e.status !== 'sent'));

    if (isOnline) {
      flushQueue();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // On isOnline transition: false → true → flush
  useEffect(() => {
    if (isOnline && !prevIsOnline.current) {
      flushQueue();
    }
    prevIsOnline.current = isOnline;
  }, [isOnline, flushQueue]);

  return { queueMessage, retryMessage, queuedMessages, flushQueue };
};

export default useOfflineMessageQueue;
